import { useState, useEffect, useRef } from 'react';
import { Send, RotateCcw, MessageCircle, BookOpen, Loader2, Download, MoreVertical, Reply } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import remarkBreaks from 'remark-breaks';
import rehypeKatex from 'rehype-katex';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import clsx from 'clsx';
import { ContextModal } from './ui/ContextModal';
import { toast } from 'react-toastify';

export function ModernChatBox({
  user,
  chat,
  setChat,
  onMainChunksUpdate,
  showContext,
  setShowContext,
  useHistory = false,
  onHistoryChange,
  hasResetHistory,
  setHasResetHistory
}) {
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false); // alleen voor de verzendknop
  const [contextModal, setContextModal] = useState({ isOpen: false, contexts: [] });
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [replyToIndex, setReplyToIndex] = useState(null);
  const [streamingIndex, setStreamingIndex] = useState(null); // << streamende bot-bubbel
  const scrollRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const inputRef = useRef(null);
  const botIndexRef = useRef(null);

  useEffect(() => {
    const resetBackendHistory = async () => {
      if (user?.ms_id && !hasResetHistory) {
        try {
          await fetch(`/api/chat/history/${user.ms_id}`, { method: 'DELETE' });
        } catch (err) {
          console.warn('⚠️ Failed to reset backend chat history:', err);
        } finally {
          setHasResetHistory(true);
        }
      }
    };
    resetBackendHistory();
  }, [user?.ms_id, hasResetHistory, setHasResetHistory]);

  const buildReplyContextPayload = () => {
    if (replyToIndex === null) return null;
    const botMsg = chat[replyToIndex];
    if (!botMsg || botMsg.type !== 'bot') return null;

    let prevUserText = '';
    if (replyToIndex - 1 >= 0 && chat[replyToIndex - 1]?.type === 'user') {
      prevUserText = chat[replyToIndex - 1].text || '';
    }
    return {
      user_text: prevUserText,
      bot_text: botMsg.text || '',
      sources: botMsg.contexts || []
    };
  };

  // ======= STREAMING SEND =======
  const sendQuestion = async () => {
    if (!message.trim() || !user?.ms_id) return;

    const msgToSend = message;
    setMessage('');
    if (inputRef.current) inputRef.current.style.height = '48px';

    // user + lege bot bubble toevoegen
    setChat(prev => {
      const next = [...prev, { type: 'user', text: msgToSend, contexts: [] }, { type: 'bot', text: '', contexts: [] }];
      const newBotIndex = next.length - 1;
      botIndexRef.current = newBotIndex;
      setStreamingIndex(newBotIndex);          // << deze bubbel streamt
      return next;
    });

    setIsLoading(true); // verzendknop uit

    try {
      const replyPayload = buildReplyContextPayload();

      const res = await fetch('/api/chat/stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache',
        },
        cache: 'no-store',
        body: JSON.stringify({
          message: msgToSend,
          ms_id: user.ms_id,
          show_context: true,
          reply_context: replyPayload,
        }),
      });

      if (!res.ok || !res.body) throw new Error(`Stream response not OK (${res.status})`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop() || '';

        for (const evt of events) {
          const line = evt.trim();
          if (!line.startsWith('data:')) continue;

          const payloadRaw = line.slice(5).trim();
          if (payloadRaw === '[DONE]') {
            setIsLoading(false);
            setStreamingIndex(null);           // << klaar → toon bronnen-knop
            setReplyToIndex(null);
            return;
          }

          let payload;
          try { payload = JSON.parse(payloadRaw); } catch { continue; }

          if (payload?.type === 'meta') {
            // we mogen contexts alvast opslaan, maar knop pas tonen als !isStreaming
            const mainChunks = (payload.sources || []).filter(s => s.type === 'main_chunk');
            onMainChunksUpdate?.(mainChunks);
            setChat(prev => {
              const next = [...prev];
              const idx = botIndexRef.current;
              if (idx != null && next[idx]) next[idx] = { ...next[idx], contexts: mainChunks };
              return next;
            });
          } else if (payload?.type === 'delta') {
            const piece = payload.delta || '';
            if (!piece) continue;
            setChat(prev => {
              const next = [...prev];
              const idx = botIndexRef.current;
              if (idx != null && next[idx]) next[idx] = { ...next[idx], text: (next[idx].text || '') + piece };
              return next;
            });
            if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
          } else if (payload?.type === 'error') {
            console.error('SSE error:', payload.message);
          }
        }
      }

      setIsLoading(false);
      setStreamingIndex(null);
      setReplyToIndex(null);
    } catch (err) {
      console.error('❌ Streaming error:', err);
      setIsLoading(false);
      setStreamingIndex(null);
      setReplyToIndex(null);
      toast.error('Er ging iets mis bij het streamen van het antwoord.');
      setChat(prev => [...prev, { type: 'bot', text: 'Sorry, er ging iets mis bij het ophalen van het antwoord.', contexts: [] }]);
    } finally {
      inputRef.current?.focus();
    }
  };
  // ==============================

  const resetChat = async () => {
    try {
      setChat([
        { type: 'bot', text: '👋 Hallo, ik ben Luna! Waarmee kan ik je helpen? Typ je vraag hieronder in de balk en ik help je graag verder.', contexts: [] }
      ]);
      setReplyToIndex(null);
      setStreamingIndex(null);
      await fetch(`/api/chat/history/${user.ms_id}`, { method: 'DELETE' });
      toast.success('Chat geschiedenis is gewist');
      setTimeout(() => inputRef.current?.focus(), 0);
    } catch (err) {
      toast.error('Fout bij het wissen van de chat geschiedenis');
    }
  };

  const exportChatToPDF = async () => {
    const el = document.getElementById('chat-export');
    if (!el) return;
    const canvas = await html2canvas(el);
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pageWidth;
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
    pdf.save(`chat-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendQuestion();
    }
  };

  const openContextModal = (contexts) => setContextModal({ isOpen: true, contexts });

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [chat, isLoading, streamingIndex]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setShowMobileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMobileMenuAction = (action) => {
    setShowMobileMenu(false);
    if (action === 'reset') resetChat();
    else if (action === 'export') exportChatToPDF();
  };

  const handleReplyClick = (idx) => {
    setReplyToIndex(prev => (prev === idx ? null : idx));
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        const len = inputRef.current.value.length;
        inputRef.current.setSelectionRange(len, len);
      }
    }, 0);
  };

  return (
    <div className="flex flex-col h-full bg-white dark:bg-dark-900 relative">
      {/* Desktop header */}
      <div className="hidden md:block p-4 border-b border-gray-200 dark:border-dark-700 flex-shrink-0">
        <div className="flex justify-end items-center gap-4">
          <button onClick={resetChat} className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-200 dark:bg-dark-700 hover:bg-gray-300 dark:hover:bg-dark-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors">
            <RotateCcw className="h-4 w-4" /> Wis Chat
          </button>
          <button onClick={exportChatToPDF} className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-200 dark:bg-dark-700 hover:bg-gray-300 dark:hover:bg-dark-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors">
            <Download className="h-4 w-4" /> Export Chat
          </button>
        </div>
      </div>

      {/* Chat messages */}
      <div id="chat-export" ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-thin p-4 space-y-6" style={{ minHeight: 0, paddingBottom: 'calc(80px + env(safe-area-inset-bottom))' }}>
        {chat.map((msg, idx) => (
          <ChatMessage
            key={idx}
            index={idx}
            message={msg}
            onContextClick={openContextModal}
            isReplyTarget={replyToIndex === idx}
            onReplyClick={() => handleReplyClick(idx)}
            isStreaming={idx === streamingIndex}     // << belangrijk
          />
        ))}

        {/* 🔥 Verwijderd: GEEN losse globale loader hieronder meer */}
      </div>

      {/* Input bar */}
      <div className="flex-shrink-0 p-4 border-t border-gray-200 dark:border-dark-700 bg-gray-50/95 dark:bg-dark-800/95 backdrop-blur-sm" style={{ position: 'sticky', bottom: 0, paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))', zIndex: 10 }}>
        <div className="flex gap-3 items-start">
          {/* Mobile menu */}
          <div className="relative md:hidden flex" ref={mobileMenuRef}>
            <button onClick={() => setShowMobileMenu(!showMobileMenu)} className="bg-gray-200 dark:bg-dark-700 hover:bg-gray-300 dark:hover:bg-dark-600 text-gray-700 dark:text-gray-300 rounded-xl transition-colors flex items-center justify-center border border-gray-200 dark:border-dark-700 flex-shrink-0" title="Menu" style={{ width: '48px', height: '48px', minHeight: '48px', boxSizing: 'border-box' }}>
              <MoreVertical className="h-5 w-5" />
            </button>
            {showMobileMenu && (
              <div className="absolute bottom-full left-0 mb-2 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-lg shadow-lg py-2 min-w-[160px] z-20">
                <button onClick={() => handleMobileMenuAction('reset')} className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700 flex items-center gap-2">
                  <RotateCcw className="h-4 w-4" /> Wis Chat
                </button>
                <button onClick={() => handleMobileMenuAction('export')} className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700 flex items-center gap-2">
                  <Download className="h-4 w-4" /> Export Chat
                </button>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 128) + 'px';
              }}
              onKeyDown={handleKeyPress}
              placeholder="Typ je vraag..."
              rows={1}
              className="w-full bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-xl focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-600 focus:border-transparent outline-none resize-none text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              style={{ height: 'auto', minHeight: '48px', maxHeight: '128px', overflowY: 'auto', scrollbarWidth: 'none', padding: '14px 16px', boxSizing: 'border-box', lineHeight: '1.4', fontSize: '16px', fontFamily: 'inherit', display: 'flex', alignItems: 'center' }}
            />
          </div>

          <button
            onClick={sendQuestion}
            disabled={!message.trim() || isLoading}
            className={clsx(
              "rounded-xl transition-all duration-200 flex items-center justify-center border flex-shrink-0",
              message.trim() && !isLoading
                ? "bg-primary-600 hover:bg-primary-700 text-white shadow-lg hover:shadow-xl border-primary-600"
                : "bg-gray-200 dark:bg-dark-700 text-gray-400 dark:text-gray-600 cursor-not-allowed border-gray-200 dark:border-dark-700"
            )}
            style={{ width: '48px', height: '48px', minHeight: '48px', boxSizing: 'border-box' }}
          >
            <Send className="h-5 w-5" />
          </button>
        </div>
      </div>

      <ContextModal
        isOpen={contextModal.isOpen}
        onClose={() => setContextModal({ isOpen: false, contexts: [] })}
        contexts={contextModal.contexts}
      />
    </div>
  );
}

function ChatMessage({ message, index, onContextClick, isReplyTarget, onReplyClick, isStreaming }) {
  const preprocessMathText = (text) => {
    if (!text) return '';
    let processed = text.replace(/\\\(([\s\S]*?)\\\)/g, (_, inner) => `$${inner}$`);
    processed = processed.replace(/\\\[([\s\S]*?)\\\]/g, (_, inner) => `$$${inner}$$`);
    return processed;
  };

  const hasContexts = message.contexts && message.contexts.length > 0;
  const isWelcomeBotMsg = message.type === 'bot' && index === 0;

  if (message.type === 'user') {
    return (
      <div className="flex items-start gap-4 justify-end">
        <div className="flex-1 max-w-3xl">
          <div className="bg-primary-600 dark:bg-primary-700 text-white rounded-lg p-4 shadow-sm">
            <p className="whitespace-pre-wrap break-words">{message.text}</p>
          </div>
        </div>
        <div className="w-8 h-8 rounded-full bg-primary-600 dark:bg-primary-700 flex items-center justify-center flex-shrink-0">
          <span className="text-white text-sm font-medium">{message.user?.name?.charAt(0)?.toUpperCase() || 'U'}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-4">
      {/* ← Avatar: toon spinner als we streamen */}
      <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
        {isStreaming ? (
          <Loader2 className="h-4 w-4 text-primary-600 dark:text-primary-400 animate-spin" />
        ) : (
          <MessageCircle className="h-4 w-4 text-primary-600 dark:text-primary-400" />
        )}
      </div>

      <div className="flex-1 max-w-3xl">
        <div className={clsx(
          "rounded-lg p-4 shadow-sm",
          isReplyTarget ? "bg-primary-50 dark:bg-primary-900/15 border border-primary-200 dark:border-primary-800" : "bg-gray-100 dark:bg-dark-800"
        )}>
          <div className="prose prose-sm dark:prose-invert max-w-none text-gray-900 dark:text-gray-100">
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkMath, remarkBreaks]}
              rehypePlugins={[rehypeKatex]}
              components={{
                h1: ({node, ...props}) => <h1 className="text-xl font-semibold mt-2 mb-3" {...props} />,
                h2: ({node, ...props}) => <h2 className="text-lg font-semibold mt-2 mb-2" {...props} />,
                h3: ({node, ...props}) => <h3 className="text-base font-semibold mt-2 mb-2" {...props} />,
                p:  ({node, ...props}) => <p className="mb-3 leading-relaxed" {...props} />,
                ul: ({node, ...props}) => <ul className="list-disc pl-6 my-2 space-y-1" {...props} />,
                ol: ({node, ...props}) => <ol className="list-decimal pl-6 my-2 space-y-1" {...props} />,
                li: ({node, ...props}) => <li className="my-0.5" {...props} />,
                a:  ({node, ...props}) => <a className="underline text-primary-600 hover:opacity-80" target="_blank" rel="noreferrer" {...props} />,
                blockquote: ({node, ...props}) => <blockquote className="border-l-4 pl-3 my-3 italic opacity-90" {...props} />,
                table: ({node, ...props}) => <table className="w-full border-collapse my-3 text-sm" {...props} />,
                th: ({node, ...props}) => <th className="border px-2 py-1 text-left bg-gray-200 dark:bg-dark-700" {...props} />,
                td: ({node, ...props}) => <td className="border px-2 py-1 align-top" {...props} />,
                code: ({inline, className, children, ...props}) => (
                  inline
                    ? <code className="px-1 py-0.5 rounded bg-gray-200 dark:bg-dark-700" {...props}>{children}</code>
                    : <pre className="p-3 rounded bg-gray-900 text-gray-100 overflow-auto my-3"><code className={className} {...props}>{children}</code></pre>
                ),
                hr: () => <hr className="my-4 border-gray-300 dark:border-dark-600" />
              }}
            >
              {preprocessMathText(message.text)}
            </ReactMarkdown>
          </div>

          <div className="mt-4 pt-3 border-t border-gray-200 dark:border-dark-700 flex items-center justify-between">
            <div>
              {/* Footer-indicator: GEEN spinner meer hier — alleen tekst + dots tijdens streamen */}
              {isStreaming ? (
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <span>Aan het typen</span>
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              ) : (
                hasContexts && (
                  <button
                    onClick={() => onContextClick(message.contexts)}
                    className="flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                  >
                    <BookOpen className="h-4 w-4" />
                    <span>Bekijk bron(nen)</span>
                  </button>
                )
              )}
            </div>

            {/* Geen 'Reageren' knop op welkomstbericht */}
            {!isWelcomeBotMsg && (
              <button
                onClick={onReplyClick}
                className={clsx(
                  "inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-md border transition-colors",
                  isReplyTarget
                    ? "bg-primary-600 border-primary-600 text-white hover:bg-primary-700"
                    : "bg-white dark:bg-dark-700 border-gray-300 dark:border-dark-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-dark-600"
                )}
                title={isReplyTarget ? "Reageren actief — klik om te annuleren" : "Reageren op dit antwoord"}
              >
                <Reply className="h-4 w-4" />
                <span>{isReplyTarget ? "Reageren (actief)" : "Reageren"}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
